import { useState } from "react";
import * as React from "react";
import {
    useTypes,
    useCodes,
    useCreateType,
    useCreateCode,
    useUpdateType,
    useUpdateCode,
    useDeleteType,
    useDeleteCode,
    useChildTypeType,
    useGetFilterType,
} from "@/hooks/useLocalStorage";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    ChevronRight,
    ChevronDown,
    Folder,
    FolderOpen,
    Tag,
    Plus,
    Edit,
    MoreVertical,
    Trash2,
    TreePine,
    List,
    Filter,
    Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
    Type,
    Code,
    insertTypeSchema,
    insertCodeSchema,
    InsertType,
    InsertCode,
} from "../shared/schema";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Switch } from "wouter";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Breadcrumb } from "./ui/breadcrumb";
import { FilterTypeSearch } from "@/widgets/searchFilterType";
import { useBreadcrumbs } from "./BreadcrumbContext";

interface HierarchicalNodeProps {
    item: Type | Code;
    itemType: "type" | "code";
    level?: number;
    onCodeClick?: (code: Code) => void;
    onAlert: (msg: string, ok: boolean) => void
}

interface TableHierarchicalNodeProps {
    topLevelTypesData: (Type)[];
    itemType: "type" | "code";
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (code: number) => void;
    onAlert: (msg: string, ok: boolean) => void
}

// Create Top-Level Type Dialog Component
// function CreateTypeDialog({ trigger }: { trigger: React.ReactNode }) {
//     const [open, setOpen] = useState(false);
//     const createTypeMutation = useCreateType();
//     const [alertOpen, setAlertOpen] = React.useState(false);
//     const [alertMessage, setAlertMessage] = React.useState("");
//     const [reqStatus, setReqStatus] = React.useState(false);

//     const form = useForm<InsertType>({
//         resolver: zodResolver(insertTypeSchema),
//         defaultValues: {
//             key: "",
//             name: "",
//             description: "",
//             parentType: "TYPES",
//         },
//     });

//     const onSubmit = async (data: InsertType) => {
//         try {
//             await createTypeMutation.mutateAsync(data);
//             setOpen(false);
//             form.reset();
//         } catch (error) {
//             console.error("Error creating type:", error);
//         }
//     };

//     React.useEffect(() => {
//         if (createTypeMutation.isSuccess === true) {
//             setOpen(false);
//             setReqStatus(createTypeMutation.isSuccess)
//             setAlertMessage(`Type Added Successfully!`);
//             setAlertOpen(true);
//             createTypeMutation.reset();
//         } else if (createTypeMutation.isError) {
//             const raw = createTypeMutation.error as Error;
//             const parsedMsg = parseApiErrorMessage(raw.message);
//             setReqStatus(createTypeMutation.isSuccess)
//             setOpen(false);
//             setAlertMessage(parsedMsg);
//             setAlertOpen(true);
//             createTypeMutation.reset();
//         }

//     }, [createTypeMutation])

//     return (
//         <div>
//             <Dialog open={open} onOpenChange={setOpen}>
//                 <DialogTrigger asChild>{trigger}</DialogTrigger>
//                 <DialogContent>
//                     <DialogHeader>
//                         <DialogTitle>Create New Type</DialogTitle>
//                     </DialogHeader>
//                     <Form {...form}>
//                         <form onSubmit={form.handleSubmit(onSubmit, (errors) => console.log("errors", errors))} className="space-y-4">
//                             <FormField
//                                 control={form.control}
//                                 name="key"
//                                 render={({ field }) => (
//                                     <FormItem>
//                                         <FormLabel>Key</FormLabel>
//                                         <FormControl>
//                                             <Input placeholder="E.g. CUSTOMER" {...field} />
//                                         </FormControl>
//                                         <FormMessage />
//                                     </FormItem>
//                                 )}
//                             />
//                             <FormField
//                                 control={form.control}
//                                 name="name"
//                                 render={({ field }) => (
//                                     <FormItem>
//                                         <FormLabel>Name</FormLabel>
//                                         <FormControl>
//                                             <Input placeholder="Display name" {...field} />
//                                         </FormControl>
//                                         <FormMessage />
//                                     </FormItem>
//                                 )}
//                             />
//                             <FormField
//                                 control={form.control}
//                                 name="description"
//                                 render={({ field }) => (
//                                     <FormItem>
//                                         <FormLabel>Description</FormLabel>
//                                         <FormControl>
//                                             <Input placeholder="Optional description" {...field} />
//                                         </FormControl>
//                                         <FormMessage />
//                                     </FormItem>
//                                 )}
//                             />
//                             {/* Parent type fixed to TYPES for top-level; keep hidden but present */}
//                             <input
//                                 type="hidden"
//                                 value="TYPES"
//                                 {...form.register("parentType")}
//                             />

//                             <div className="flex justify-end space-x-2">
//                                 <Button
//                                     type="button"
//                                     variant="outline"
//                                     disabled={createTypeMutation.isPending}
//                                     onClick={() => setOpen(false)}
//                                 >
//                                     Cancel
//                                 </Button>
//                                 <Button
//                                     type="submit"
//                                     className="bg-workfinity-500 hover:bg-workfinity-600"
//                                     disabled={createTypeMutation.isPending}
//                                 >
//                                     {createTypeMutation.isPending ? (
//                                         <>
//                                             <Loader2 className="mr-2 h-4 w-4 animate-spin" />
//                                             Creating Type...
//                                         </>
//                                     ) : (
//                                         "Create Type"
//                                     )}
//                                 </Button>
//                             </div>
//                         </form>
//                     </Form>
//                 </DialogContent>
//             </Dialog>
//             <AlertDialog
//                 open={alertOpen}
//                 onOpenChange={setAlertOpen}
//                 message={alertMessage}
//                 status={reqStatus}
//             />
//         </div>
//     );
// }

// Add Child Dialog Component
function AddChildDialog({
    parent,
    parentType,
    trigger,
}: {
    parent: Type | Code;
    parentType: "type" | "code";
    trigger: React.ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);

    const { data: allCodes } = useCodes();
    const createCodeMutation = useCreateCode();

    const parentTypeKey =
        parentType === "type" ? (parent as Type).name : (parent as Code).name;
    const childTypeCandidate = React.useMemo(() => {
        if (parentType === "type") return parentTypeKey;
        const candidates = Array.from(
            new Set(
                (allCodes || [])
                    .filter((c) => c.parentType === parentTypeKey)
                    .map((c) => c.type)
            )
        );
        return candidates[0] || parentTypeKey;
    }, [parentType, parentTypeKey, allCodes]);

    const form = useForm<InsertCode>({
        resolver: zodResolver(insertCodeSchema),
        defaultValues: {
            type: childTypeCandidate,
            name: "",
            description: "",
            seq: 0,
            useFlag: 1,
            parentType: parentType === "code" ? (parent as Code).type : undefined,
            parentCode: parentType === "code" ? (parent as Code).code : undefined,
        },
    });

    const onSubmit = async (data: InsertCode) => {
        try {
            await createCodeMutation.mutateAsync(data);
            setOpen(false);
            form.reset();
        } catch (error) {
            console.error("Error creating child code:", error);
        }
    };

    React.useEffect(() => {
        if (createCodeMutation.isSuccess === true) {
            setOpen(false);
            setAlertMessage(`Child Added to ${parentType === "type" ? (parent as Type).name : (parent as Code).name} Successfully!`);
            setReqStatus(createCodeMutation.isSuccess)
            setAlertOpen(true);
            createCodeMutation.reset();
        } else if (createCodeMutation.isError) {
            const raw = createCodeMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(createCodeMutation.isSuccess)
            setOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            createCodeMutation.reset();
        }

    }, [createCodeMutation])

    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>{trigger}</DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Add Child Code to{" "}
                            {parentType === "type"
                                ? (parent as Type).name
                                : (parent as Code).name}
                        </DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="type"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Child Type</FormLabel>
                                        <Select value={field.value} onValueChange={() => { }}>
                                            <FormControl>
                                                <SelectTrigger disabled>
                                                    <SelectValue placeholder="Child type" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value={childTypeCandidate}>
                                                    ({childTypeCandidate})
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Code is auto-assigned; no manual code field */}
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter description" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="flex justify-end space-x-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setOpen(false)}
                                    disabled={createCodeMutation.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="bg-workfinity-500 hover:bg-workfinity-600"
                                    disabled={createCodeMutation.isPending}
                                >
                                    {createCodeMutation.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Adding Child...
                                        </>
                                    ) : (
                                        "Add Child"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </div>
    );
}

// Edit Item Dialog Component
function EditItemDialog({
    item,
    itemType,
    trigger,
    onAlert
}: {
    item: Type | Code;
    itemType: "type" | "code";
    trigger: React.ReactNode;
    onAlert: (msg: string, ok: boolean) => void
}) {
    const [open, setOpen] = useState(false);
    // const [alertOpen, setAlertOpen] = React.useState(false);
    // const [alertMessage, setAlertMessage] = React.useState("");
    // const [reqStatus, setReqStatus] = React.useState(false);

    const updateTypeMutation = useUpdateType();
    const updateCodeMutation = useUpdateCode();

    const isType = itemType === "type";
    const typeItem = item as Type;
    const codeItem = item as Code;

    const typeForm = useForm<InsertType>({
        resolver: zodResolver(insertTypeSchema),
        defaultValues: {
            key: isType ? typeItem.type : "",
            name: isType ? typeItem.name : "",
            description: isType ? typeItem.description || "" : "",
            parentType: isType ? typeItem.parentType || "" : "",
            parentCode: isType ? typeItem.parentCode || "" : "",
            code: isType ? typeItem.code || "" : "",
        },
    });

    const codeForm = useForm<InsertCode>({
        resolver: zodResolver(insertCodeSchema),
        defaultValues: {
            type: !isType ? codeItem.type : "",
            code: !isType ? codeItem.code : "",
            name: !isType ? codeItem.name : "",
            description: !isType ? codeItem.description || "" : "",
            seq: !isType ? codeItem.seq : 0,
            useFlag: !isType ? codeItem.useFlag : 1,
            parentType: !isType ? codeItem.parentType : undefined,
            parentCode: !isType ? codeItem.parentCode : undefined,
        },
    });

    const onSubmitType = async (data: InsertType) => {
        console.log("data", data)
        try {
            await updateTypeMutation.mutateAsync({
                key: typeItem.code!,
                updates: data,
            });
            setOpen(false);
        } catch (error) {
            console.error("Error updating type:", error);
        }
    };

    const onSubmitCode = async (data: InsertCode) => {
        try {
            await updateCodeMutation.mutateAsync({
                type: codeItem.type,
                code: codeItem.code,
                updates: data,
            });
            setOpen(false);
        } catch (error) {
            console.error("Error updating code:", error);
        }
    };

    React.useEffect(() => {
        if (updateTypeMutation.isSuccess === true) {
            setOpen(false);
            onAlert("Type Edited successfully!", true);
            // setReqStatus(updateTypeMutation.isSuccess)
            // setAlertMessage(`Type Edited Successfully!`);
            // setAlertOpen(true);
            updateTypeMutation.reset();
        } else if (updateTypeMutation.isError) {
            const raw = updateTypeMutation.error as Error;
            // const parsedMsg = parseApiErrorMessage(raw.message);
            onAlert(parseApiErrorMessage(raw.message), false);
            // setReqStatus(updateTypeMutation.isSuccess)
            setOpen(false);
            // setAlertMessage(parsedMsg);
            // setAlertOpen(true);
            updateTypeMutation.reset();
        }
    }, [updateTypeMutation])

    React.useEffect(() => {
        if (updateCodeMutation.isSuccess === true) {
            setOpen(false);
            onAlert("Code Edited successfully!", true);
            // setReqStatus(updateCodeMutation.isSuccess)
            // setAlertMessage(`Code Edited Successfully!`);
            // setAlertOpen(true);
            updateCodeMutation.reset();
        } else if (updateCodeMutation.isError) {
            const raw = updateCodeMutation.error as Error;
            // const parsedMsg = parseApiErrorMessage(raw.message);
            onAlert(parseApiErrorMessage(raw.message), false);
            // setReqStatus(updateCodeMutation.isSuccess)
            setOpen(false);
            // setAlertMessage(parsedMsg);
            // setAlertOpen(true);
            updateCodeMutation.reset();
        }
    }, [updateCodeMutation])

    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>{trigger}</DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Edit {isType ? "Type" : "Code"}:{" "}
                            {isType ? typeItem.name : codeItem.name}
                        </DialogTitle>
                    </DialogHeader>
                    {isType ? (
                        <Form {...typeForm}>
                            <form
                                onSubmit={typeForm.handleSubmit(onSubmitType, (errors) => console.log("errors", errors))}
                                className="space-y-4"
                            >
                                <FormField
                                    control={typeForm.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Name</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter name" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={typeForm.control}
                                    name="description"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Description</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter description" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <div className="flex justify-end space-x-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={updateTypeMutation.isPending}
                                        onClick={() => setOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={updateTypeMutation.isPending}
                                        className="bg-workfinity-500 hover:bg-workfinity-600"
                                    >
                                        {updateTypeMutation.isPending ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Updating Type...
                                            </>
                                        ) : (
                                            "Update Type"
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    ) : (
                        <Form {...codeForm}>
                            <form
                                onSubmit={codeForm.handleSubmit(onSubmitCode, (errors) => console.log("errorscode", errors))}
                                className="space-y-4"
                            >
                                <FormField
                                    control={codeForm.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Name</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter name" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={codeForm.control}
                                    name="description"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Description</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter description" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <div className="flex justify-end space-x-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={updateCodeMutation.isPending}
                                        onClick={() => setOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="bg-workfinity-500 hover:bg-workfinity-600"
                                        disabled={updateCodeMutation.isPending}
                                    >
                                        {updateCodeMutation.isPending ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Updating code...
                                            </>
                                        ) : (
                                            "Update Code"
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    )}
                </DialogContent>
            </Dialog>
            {/* <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            /> */}
        </div>
    );
}

interface PopupProps {
    popupName: string;
    heading: Type | Code | undefined;
    content: string | undefined;
    onClose: () => void;
}

function Popup({ popupName, heading, content, onClose }: PopupProps) {

    const isCode =
        typeof content === "string" &&
        (content.includes("\\n") || content.includes("{") || content.includes("}"));

    const formattedContent = isCode ? content.replace(/\\r?\\n/g, "\n") : content;

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{popupName}</DialogTitle>
                </DialogHeader>

                <div className="mt-4 space-y-4 text-sm text-gray-800">
                    <div>
                        <label className="font-semibold text-black">Name :</label>
                        <div className="border border-gray-400 rounded-md p-2 mt-1 bg-white">
                            {heading?.name || ""}
                        </div>
                    </div>

                    <div>
                        <label className="font-semibold text-black">
                            {popupName}
                        </label>
                        <div className="min-h-[200px] border border-gray-400 rounded-md p-2 mt-1 bg-white whitespace-pre-wrap font-mono text-sm">
                            {formattedContent || ""}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function TableHierarchicalNode({ topLevelTypesData, itemType, pageIndex, pageSize, totalCount, onPageChange, onAlert }: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<(Type | Code)[]>([]);
    const [typeItem, setTypeItem] = useState<("type" | "code")>("type");
    const [breadcrumbPath, setBreadcrumbPath] = useState<(Type | Code)[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Type | Code | null>(null);

    const [parentType, setParentType] = useState<string>("");
    const [parentCode, setParentCode] = useState<string>("");

    const [showPopup, setShowPopup] = React.useState(false);
    const [dataHead, setDataHead] = React.useState<Type | Code | undefined>();
    const [popupName, setPopupName] = React.useState("");
    const [dataContent, setDataContent] = React.useState<string | undefined>("");
    const [childtypeval, setChildtypeval] = React.useState<Type[]>([]);
    const [childFetchTick, setChildFetchTick] = React.useState(0);

    const { data: typeChildren, isLoading, refetch: refetchChildTypes, } = useChildTypeType(parentType, parentCode);

    React.useEffect(() => {
        if (typeChildren?.length ?? [].length > 0) {
            setChildtypeval(typeChildren ?? []);
        }
    }, [typeChildren]);

    const { data: allCodes } = useCodes();
    const deleteTypeMutation = useDeleteType();
    const deleteCodeMutation = useDeleteCode();

    React.useEffect(() => {
        if (topLevelTypesData) {
            setCurrentData(topLevelTypesData);
            setTypeItem(itemType)
        }
    }, [topLevelTypesData]);

    function handleView(item: Type | Code) {
        setBreadcrumbPath((prev) => [...prev, item]);
        setParentType(item.name);
        setParentCode(item.code ?? "");
        refetchChildTypes();

        const codeChildren =
            item.parentType === "TYPES"
                ? allCodes?.filter((code) => code.type === (item as Type).name) || []
                : allCodes?.filter(
                    (code) =>
                        code.type === (item as Code).name &&
                        code.parentCode === (item as Code).code
                ) || [];

        setCurrentData(codeChildren);
        setChildFetchTick((v) => v + 1);
    }

    React.useEffect(() => {
        if (!isLoading && (parentType || parentCode)) {
            const codeChildren =
                breadcrumbPath[breadcrumbPath.length - 1]?.parentType === "TYPES"
                    ? allCodes?.filter(
                        (code) => code.type === (breadcrumbPath[breadcrumbPath.length - 1] as Type).name
                    ) || []
                    : allCodes?.filter(
                        (code) =>
                            code.type === (breadcrumbPath[breadcrumbPath.length - 1] as Code)?.name &&
                            code.parentCode === (breadcrumbPath[breadcrumbPath.length - 1] as Code).code
                    ) || [];

            const combined = [...(typeChildren || []), ...codeChildren];
            setCurrentData(combined);
        }
    }, [typeChildren, isLoading, allCodes, childFetchTick,]);

    function subItems(item: Type | Code): number {

        const childItems =
            item.type === "TYPES"
                ? allCodes?.filter((code) => code.type === (item as Type).name) || []
                : allCodes?.filter(
                    (code) =>
                        code.type === (item as Code).name &&
                        code.parentCode === (item as Code).code
                ) || [];
        return childItems.length || 0
    }
    console.log("currentData", currentData)
    function handleBreadcrumbClick(index: number) {
        const newPath = breadcrumbPath.slice(0, index + 1);
        setBreadcrumbPath(newPath);

        const lastItem = newPath[newPath.length - 1];

        if (!lastItem) {
            setCurrentData(topLevelTypesData || []);
            return;
        }

        setParentType(lastItem.name);
        setParentCode(lastItem.code ?? "");

        const codeChildren =
            lastItem.parentType === "TYPES"
                ? allCodes?.filter((code) => code.type === (lastItem as Type).name) || []
                : allCodes?.filter(
                    (code) =>
                        code.type === (lastItem as Code).name &&
                        code.parentCode === (lastItem as Code).code
                ) || [];

        setCurrentData(codeChildren);
    }


    function handleDeleteClick(e: React.MouseEvent, item: Type | Code) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;
        console.log("itemToDelete", itemToDelete)
        if (itemToDelete.type === "TYPES") {
            deleteTypeMutation.mutate({
                key: itemToDelete?.code!,
                force: currentData.length > 0,
            });
        } else {
            deleteCodeMutation.mutate({
                type: itemToDelete.type!,
                code: itemToDelete.code!,
            });
        }

    }

    React.useEffect(() => {
        if (deleteTypeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            onAlert("Type Deleted Successfully!", true);
            deleteTypeMutation.reset();
        } else if (deleteTypeMutation.isError) {
            const raw = deleteTypeMutation.error as Error;
            setDeleteDialogOpen(false);
            onAlert(parseApiErrorMessage(raw.message), false);
            deleteTypeMutation.reset();
        }
    }, [deleteTypeMutation])

    React.useEffect(() => {
        if (deleteCodeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            onAlert("Code Deleted Successfully!", true);
            deleteCodeMutation.reset();
        } else if (deleteCodeMutation.isError) {
            const raw = deleteCodeMutation.error as Error;
            setDeleteDialogOpen(false);
            onAlert(parseApiErrorMessage(raw.message), false);
            deleteCodeMutation.reset();
        }
    }, [deleteTypeMutation])

    return (
        <div className="grid grid-cols-[auto_1fr] gap-4">
            <div className="border bg-white border-gray-200 rounded-lg p-4">
                <div className="flex flex-col space-y-2">
                    <Button
                        variant={breadcrumbPath.length === 0 ? "secondary" : "outline"}
                        size="sm"
                        className="w-7 h-7 md:w-14 md:h-8 p-0"
                        onClick={() => {
                            setBreadcrumbPath([]);
                            setCurrentData(topLevelTypesData);
                        }}
                    >
                        Types
                    </Button>

                    {breadcrumbPath.map((item, index) => (
                        <div key={index} style={{ marginLeft: `${(index + 1) * 16}px` }} className={`relative pl-6`}>
                            <span className="absolute left-0 top-1/2 w-4 h-0.5 bg-gray-400"></span>
                            <span className="absolute left-0 top-0 h-1/2 w-0.5 bg-gray-400"></span>

                            <Button
                                variant={index === breadcrumbPath.length - 1 ? "secondary" : "outline"}
                                size="sm"
                                onClick={() => handleBreadcrumbClick(index)}
                            >
                                {item.name}
                            </Button>
                        </div>
                    ))}
                </div>

            </div>

            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table >
                    <TableHeader>
                        <TableRow>
                            <TableHead>Select</TableHead>
                            <TableHead>ID</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Date & Time</TableHead>
                            <TableHead>Items</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow key={`${data.code}-${data.type}`}>
                                <TableCell className="font-mono">
                                    {(data.hasChildTypes || subItems(data) > 0) ?
                                        <Button
                                            size="sm"
                                            variant={"link"}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleView(data);
                                            }}
                                        >
                                            View Item
                                        </Button> :
                                        <span className="pl-3">{data?.type === "TYPES" && "No Item"}</span>

                                    }
                                </TableCell>
                                <TableCell className="font-mono">
                                    {data.code}
                                </TableCell>
                                <TableCell className="font-mono font-medium">
                                    {data.name}
                                </TableCell>
                                <TableCell className="max-w-xs truncate">
                                    {/* {data.description} */}
                                    <Button
                                        variant={"link"}
                                        onClick={() => {
                                            setPopupName(`${data.type === "TYPES" ? "Type Description" : "Code Description"}`)
                                            setDataHead(data);
                                            setDataContent(data.description);
                                            setShowPopup(true);
                                        }}
                                    >
                                        View
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    {new Date(data.createdAt).toLocaleString()}
                                </TableCell>
                                <TableCell>
                                    {data?.type === "TYPES" && subItems(data) + data?.childTypesCount}
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center space-x-2">
                                        {data.type === "TYPES" ? (
                                            <AddChildDialog
                                                parent={data}
                                                parentType={data.type === "TYPES" ? "type" : "code"}
                                                trigger={
                                                    <button
                                                        onClick={(e) => e.stopPropagation()}
                                                        title="Add Child Code"
                                                        className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                                                    >
                                                        <Plus size={20} strokeWidth={2.5} />
                                                    </button>
                                                }
                                            />
                                        ) : (
                                            <div className="w-9 h-9 md:w-10 md:h-10" />
                                        )}

                                        <EditItemDialog
                                            item={data}
                                            itemType={data.type === "TYPES" ? "type" : "code"}
                                            onAlert={onAlert}
                                            trigger={
                                                <button
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={
                                                        data?.type === "TYPES"
                                                            ? "Edit this Type"
                                                            : "Edit this Code"
                                                    }
                                                    className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                                                >
                                                    <Edit size={20} strokeWidth={2.5} />
                                                </button>
                                            }
                                        />

                                        <button
                                            onClick={(e) => handleDeleteClick(e, data)}
                                            title={
                                                data?.type === "TYPES"
                                                    ? "Delete this Type"
                                                    : "Delete this Code"
                                            }
                                            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100 text-red-600"
                                        >
                                            <Trash2 size={20} strokeWidth={2.5} />
                                        </button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {
                    breadcrumbPath.length === 0 &&
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                }

            </div>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={itemToDelete?.type === "TYPES" ? deleteTypeMutation.isPending : deleteCodeMutation.isPending}
                itemName={itemToDelete?.type === "TYPES" ? (itemToDelete as Type).name : (itemToDelete as Code)?.name}
                itemType={itemToDelete?.type === "TYPES" ? "type" : "code"}
            />

            {/* <AlertDialog
                open={alertOpen}
                onOpenChange={() => setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            /> */}

            {showPopup && (
                <Popup
                    popupName={popupName}
                    heading={dataHead}
                    content={dataContent}
                    onClose={() => setShowPopup(false)}
                />
            )}

        </div >
    );
}

function HierarchicalNode({ item, itemType, level = 0, onCodeClick, onAlert }: HierarchicalNodeProps) {

    const [expanded, setExpanded] = useState(false);
    const [expandRequested, setExpandRequested] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Type | Code | null>(null);

    const [children, setChildren] = useState<Type[]>([]);
    const [typeChildren, setTypeChildren] = useState<Type[]>([]);
    const [hasChildren, setHasChildren] = useState(false);
    const [loading, setLoading] = useState(false);
    const [parentType, setParentType] = useState("");
    const [parentCode, setParentCode] = useState("");

    const { data: allCodes } = useCodes();
    const deleteTypeMutation = useDeleteType();
    const deleteCodeMutation = useDeleteCode();
    const { data: child, isLoading } = useChildTypeType(
        parentType,
        parentCode,
    );

    React.useEffect(() => {
        const childItems =
            itemType === "type"
                ? allCodes?.filter((code) => {
                    const matches = code.type === (item as Type).name;
                    return matches;
                }) || []
                : allCodes?.filter((code) => {
                    const matches =
                        code.type === (item as Code).name &&
                        code.parentCode === (item as Code).code;
                    return matches;
                }) || [];
        setChildren(childItems)
        setHasChildren(childItems.length > 0)
    }, [allCodes])

    const handleToggleExpand = (type: Type | Code) => {
        if (!expanded) {
            if (typeChildren.length === 0 && type?.hasChildTypes) {
                setLoading(true);
                setParentType(type.name);
                setParentCode(type.code ?? "");
                setExpandRequested(true);
            } else if (hasChildren) {
                setExpanded(true);
            } else if (type?.hasChildTypes) {
                setExpanded(true);
            } else {
                setExpanded(false);
            }
        } else {
            setExpanded(false);
        }
    };


    React.useEffect(() => {
        if (!isLoading) {
            if (child && child?.length > 0) {
                setTypeChildren(child)
                setLoading(false);
                if (expandRequested) {
                    setExpanded(true);
                    setExpandRequested(false);
                }
            } else if (hasChildren && expandRequested) {
                setLoading(false);
                setExpanded(true)
                setExpandRequested(false);
            } else {
                setLoading(false);
                setExpanded(false)
            }
        }
    }, [child])

    const getIcon = () => {
        if (itemType === "type") {
            return expanded ? (
                <FolderOpen className="h-4 w-4 text-blue-600" />
            ) : (
                <Folder className="h-4 w-4 text-blue-600" />
            );
        } else if (item.type === "TYPES") {
            return expanded ? (
                <FolderOpen className="h-4 w-4 text-amber-600" />
            ) : (
                <Folder className="h-4 w-4 text-amber-600" />
            );
        } else {
            return <Tag className="h-4 w-4 text-red-600" />;
        }
    };

    const getIconBackground = () => {
        if (itemType === "type") {
            return "bg-blue-100";
        } else if (item.type === "TYPES") {
            return "bg-blue-100";
        } else {
            return "bg-green-100";
        }
    };

    const canHaveChildren = itemType === "type" || item.type === "TYPES"

    function handleDeleteClick(e: React.MouseEvent, item: Type | Code) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;
        console.log("itemToDelete", itemToDelete)
        if (itemToDelete.type === "TYPES") {
            deleteTypeMutation.mutate({
                key: itemToDelete?.code!,
            });
        } else {
            deleteCodeMutation.mutate({
                type: itemToDelete.type!,
                code: itemToDelete.code!,
            });
        }

    }

    React.useEffect(() => {
        if (deleteTypeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            onAlert("Type Deleted successfully!", true);
            deleteTypeMutation.reset();
        } else if (deleteTypeMutation.isError) {
            const raw = deleteTypeMutation.error as Error;
            onAlert(parseApiErrorMessage(raw.message), false);
            setDeleteDialogOpen(false);
            deleteTypeMutation.reset();
        }
    }, [deleteTypeMutation])

    React.useEffect(() => {
        if (deleteCodeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            onAlert("Code Deleted successfully!", true);
            deleteCodeMutation.reset();
        } else if (deleteCodeMutation.isError) {
            const raw = deleteCodeMutation.error as Error;
            onAlert(parseApiErrorMessage(raw.message), true);
            setDeleteDialogOpen(false);
            deleteCodeMutation.reset();
        }
    }, [deleteCodeMutation])


    return (
        <div className="border bg-white border-gray-200 rounded-lg">
            <div
                className="flex items-center space-x-2 md:space-x-3 p-2 md:p-3 hover:bg-gray-50 cursor-pointer"
                style={{ paddingLeft: `${8 + level * 16}px` }}
            >
                {(hasChildren || item?.hasChildTypes) && (
                    <button
                        className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleExpand(item);
                        }}
                    >
                        {loading ? (
                            <span className="h-4 w-4 animate-spin">⏳</span>
                        ) : expanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </button>
                )}

                {!(hasChildren || item?.hasChildTypes) && <div className="w-6" />}

                <div
                    className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center",
                        getIconBackground()
                    )}
                >
                    {getIcon()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                        <span className="font-medium text-gray-900 truncate text-sm md:text-base">
                            {itemType === "type" ? (item as Type).name : (item as Code).name}
                        </span>
                        <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                            {itemType === "type" ? (item as Type).code : (item as Code).code}
                        </span>
                    </div>
                    {item.description && (
                        <div className="text-xs md:text-sm text-gray-500 truncate mt-1">
                            {item.description}
                        </div>
                    )}
                </div>

                <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                    {hasChildren && (
                        <span className="text-xs text-gray-500 hidden sm:inline">
                            {children.length} items
                        </span>
                    )}

                    {canHaveChildren && (
                        <AddChildDialog
                            parent={item}
                            parentType={itemType}
                            trigger={
                                <button
                                    onClick={(e) => e.stopPropagation()}
                                    title="Add Child Code"
                                    className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                                >
                                    <Plus size={20} strokeWidth={2.5} />
                                </button>
                            }
                        />
                    )}

                    <EditItemDialog
                        item={item}
                        itemType={itemType}
                        trigger={
                            <button
                                onClick={(e) => e.stopPropagation()}
                                title={
                                    item?.type === "TYPES"
                                        ? "Edit this Type"
                                        : "Edit this Code"
                                }
                                className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                            >
                                <Edit size={20} strokeWidth={2.5} />
                            </button>
                        }
                        onAlert={onAlert}
                    />
                    <button
                        onClick={(e) => handleDeleteClick(e, item)}
                        title={
                            item?.type === "TYPES"
                                ? "Delete this Type"
                                : "Delete this Code"
                        }
                        className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100 text-red-600"
                    >
                        <Trash2 size={20} strokeWidth={2.5} />
                    </button>


                </div>
            </div>

            {expanded && (children.length > 0 || typeChildren.length > 0) && (
                <div className="border-t border-gray-200 bg-gray-50">
                    <div className="py-2 space-y-1">
                        {typeChildren.map((childType) => (
                            <HierarchicalNode
                                key={`type-${childType.code}`}
                                item={childType}
                                itemType="type"
                                level={level + 1}
                                onCodeClick={onCodeClick}
                                onAlert={onAlert}
                            />
                        ))}
                        {children.map((childCode) => (
                            <HierarchicalNode
                                key={`code-${childCode.name}-${childCode.code}`}
                                item={childCode}
                                itemType="code"
                                level={level + 1}
                                onCodeClick={onCodeClick}
                                onAlert={onAlert}
                            />
                        ))}
                    </div>
                </div>
            )}


            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={itemToDelete?.type === "TYPES" ? deleteTypeMutation.isPending : deleteCodeMutation.isPending}
                itemName={itemToDelete?.type === "TYPES" ? (itemToDelete as Type).name : (itemToDelete as Code)?.name}
                itemType={itemToDelete?.type === "TYPES" ? "type" : "code"}
            />
        </div>
    );
}

type FilterType = {
    key: string;
    name: string;
    [key: string]: any;
};

export function TableExplorer() {
    const [viewMode, setViewMode] = useState<"table" | "tree">("tree");
    const [topLevelTypes, setTopLevelTypes] = useState<(Type)[]>([]);

    const [typeInput, setTypeInput] = useState<string>("ALL");
    const [typeFilter, setTypeFilter] = useState<string>("ALL");
    const [filterOpen, setFilterOpen] = useState(false);

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);

    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(10);

    const [filterName, setFilterName] = useState<string>("");
    const [filterType, setFilterType] = useState<string>("");
    const [filterTypeData, setFilterTypeData] = useState<FilterType[]>([]);

    const { data: types, isLoading } = useTypes(
        pageIndex,
        pageSize,
        typeFilter === "ALL" ? undefined : typeFilter
    );

    const { data: filterTypes, isFetching: isFilterFetching } = useGetFilterType(
        filterName.length >= 3 ? filterName : undefined,
        filterType === "" ? undefined : filterType,
    );

    React.useEffect(() => {
        if (filterTypes && filterTypes.length > 0) {
            setFilterTypeData(filterTypes)
        } else {
            setFilterTypeData([])
        }
    }, [filterTypes])

    React.useEffect(() => {
        setTopLevelTypes(types?.items ?? [])
        setTotalCount(types?.total ?? 0)
    }, [types])

    const { setBreadcrumbs } = useBreadcrumbs();

    React.useEffect(() => {
        setBreadcrumbs([
            {
                label: `Code Manager`,
                path: `/`,
            },
        ]);
    }, []);

    // Check for navigation from search
    React.useEffect(() => {
        const selectedItemStr = localStorage.getItem("cm:selectedItem");
        if (selectedItemStr) {
            try {
                const selectedItem = JSON.parse(selectedItemStr);
                console.log("Navigate to item in TreeExplorer:", selectedItem);
                // Clear the selection after handling
                localStorage.removeItem("cm:selectedItem");

                // TODO: Expand tree to show the selected item
                // This could involve expanding parent nodes and highlighting the target
            } catch (error) {
                console.error("Error parsing selected item:", error);
                localStorage.removeItem("cm:selectedItem");
            }
        }
    }, []);

    const handleCodeClick = (code: Code) => {
        console.log("Code clicked:", code);
    };

    const handleSearch = () => {
        setTypeFilter(typeInput);
    };

    const handleRefresh = (item: boolean) => {
        setTypeInput("ALL");
        setTypeFilter("ALL");
        if (item) setFilterOpen(false)
    };

    const showAlert = (message: string, status: boolean) => {
        setAlertMessage(message);
        setReqStatus(status);
        setAlertOpen(true);
    };


    return (
        <div className="min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-xl md:text-2xl font-semibold text-black">
                            Code Manager
                        </h2>
                        <p className="text-black mt-1 text-sm md:text-base">
                            Manage codes within types
                        </p>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            <Button
                                size="sm"
                                variant={viewMode === "table" ? "default" : "ghost"}
                                onClick={() => setViewMode("table")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <List className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Table</span>
                                <span className="sm:hidden">Table</span>
                            </Button>
                            <Button
                                size="sm"
                                variant={viewMode === "tree" ? "default" : "ghost"}
                                onClick={() => setViewMode("tree")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <TreePine className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Tree</span>
                                <span className="sm:hidden">Tree</span>
                            </Button>
                        </div>
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={() => setFilterOpen(true)}
                        >
                            Filter Type
                        </Button>
                    </div>
                </div>
            </div>
            <div>
                {filterOpen &&
                    <Card className="mb-4 md:mb-6">
                        <CardContent className="pt-0">
                            <FilterTypeSearch
                                filterTypeData={filterTypeData}
                                isLoading={isFilterFetching}
                                value={typeInput}
                                onChange={setTypeInput}
                                onSearch={(query) => setFilterName(query)}
                            />

                            <div className="mt-4 flex justify-center space-x-2">
                                <Button onClick={handleSearch}>Search</Button>
                                <Button variant="outline" onClick={() => handleRefresh(false)}>
                                    Refresh
                                </Button>
                                <Button variant="outline" onClick={() => handleRefresh(true)}>
                                    Close
                                </Button>
                            </div>

                        </CardContent>
                    </Card>
                }
            </div>

            {!isLoading ?
                viewMode === "table" ? (
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <div className="space-y-2">
                                {topLevelTypes && topLevelTypes.length > 0 ? (
                                    <>
                                        <TableHierarchicalNode
                                            topLevelTypesData={topLevelTypes}
                                            itemType="type"
                                            pageIndex={pageIndex}
                                            pageSize={pageSize}
                                            totalCount={totalCount}
                                            onPageChange={setPageIndex}
                                            onAlert={showAlert}
                                        />

                                    </>

                                ) : (
                                    <div className="text-center py-8 text-black">
                                        <p>
                                            No types with codes found. Create types and codes to get
                                            started.
                                        </p>
                                    </div>
                                )}
                                <AlertDialog
                                    open={alertOpen}
                                    onOpenChange={setAlertOpen}
                                    message={alertMessage}
                                    status={reqStatus}
                                />
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <div className="space-y-2">
                                {topLevelTypes && topLevelTypes.length > 0 ? (
                                    topLevelTypes.map((type) => (
                                        <HierarchicalNode
                                            key={type.key}
                                            item={type}
                                            itemType="type"
                                            onCodeClick={handleCodeClick}
                                            onAlert={showAlert}
                                        />
                                    ))

                                ) : (
                                    <div className="text-center py-8 text-black">
                                        <p>
                                            No types with codes found. Create types and codes to get
                                            started.
                                        </p>
                                    </div>
                                )}
                                <PaginationControl
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                />
                                <AlertDialog
                                    open={alertOpen}
                                    onOpenChange={setAlertOpen}
                                    message={alertMessage}
                                    status={reqStatus}
                                />
                            </div>
                        </CardContent>
                    </Card>
                )
                : <div className="p-6 text-black">Loading...</div>}
        </div>

    );
}
